// SmartSend part B — test-send phone numbers. Pure module, exercised directly by the self-test.
//
// The pattern is the CONTRACT's, verbatim (§5 `PUT WhatsApp/TestSend/{id}`): `^(05\d{8}|9725\d{8})$`
// — a local 10-digit mobile, or the same number in E.164 without the `+`. The server answers 422
// on anything else; validating with the identical expression here means the operator is told
// before a charge is attempted, and never told something the server would disagree with.
//
// 🔴 A test send is a REAL, CHARGED send (invariant 9). There is no sandbox — which is why the
// dialog shows the rendered text first and caps the list at two numbers.

export const WA_PHONE_RE = /^(05\d{8}|9725\d{8})$/;

export const MAX_TEST_PHONES = 2;

// Strip what a human types and a copy/paste carries: spaces, dashes, parentheses, a leading
// `+`, and RTL/LTR marks (U+200E/U+200F), which Hebrew keyboards inject around digits and which
// would otherwise fail the pattern invisibly — the field would look correct and still be refused.
export const normalizePhone = (raw: string): string =>
    (raw ?? '')
        .replace(/[\s\-()\u200e\u200f\u202a-\u202e]/g, '')
        .replace(/^\+/, '');

export const isValidWaPhone = (raw: string): boolean => WA_PHONE_RE.test(normalizePhone(raw));

// localStorage key of the last numbers used, per user. Namespaced with the user id so a shared
// workstation never offers one operator's mobile to another (the same reason the key carries a
// version suffix: a shape change must not try to read the old value).
export const testRecipientsKey = (userId: string | number | null | undefined): string =>
    `smartsend.wa.preview.recipients.v1::${userId ?? 'anon'}`;

export const loadTestRecipients = (userId: string | number | null | undefined): string[] => {
    try {
        const raw = window.localStorage.getItem(testRecipientsKey(userId));
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed)
            ? parsed.filter((p: any) => typeof p === 'string').slice(0, MAX_TEST_PHONES)
            : [];
    } catch {
        // Private mode, a cleared store, or a value another version wrote. An empty list is a
        // correct answer for all three; a throw here would take the dialog down with it.
        return [];
    }
};

export const saveTestRecipients = (userId: string | number | null | undefined, phones: string[]): void => {
    try {
        window.localStorage.setItem(
            testRecipientsKey(userId),
            JSON.stringify(phones.filter(isValidWaPhone).slice(0, MAX_TEST_PHONES)),
        );
    } catch {
        // Convenience only — never let a storage quota or a blocked store fail a send.
    }
};

// '0521234567' → '05*-***4567'. Mirrors the server's own `ToNumberMasked` shape (§5) so the
// dialog and the preview cannot disagree about how a number is written.
export const maskPhone = (raw: string): string => {
    const p = normalizePhone(raw);
    if (p.length < 4) return p;
    return `${p.slice(0, 2)}*-***${p.slice(-4)}`;
};
